// lib
import React, { useReducer, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { isEqual } from 'lodash'
import { Transition } from 'react-transition-group'

import ADND from '@aytm/lib/adnd/ADND'

import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

import { viewerIsAdmin } from '@aytm/app/stats/lib/page/viewer'
import { numberToWords } from '@aytm/app/stats/lib/util/number-to-words'

import BasicFilters from '@aytm/app/stats/lib/query/basic-filters'
import { dataReducer } from '@aytm/app/stats/page/question-views/vq/lib/reducers'

import { generateUID } from '@aytm/app/stats/lib/util/helpers'
import { getLogicData, getRefs, getClustersRefs } from '@aytm/app/stats/lib/query/refs'

// components
import { default as ComponentContainer } from '@aytm/app/stats/page/question-views/components/questions-custom/Component'

import { Checkbox } from 'primereact/checkbox'
import { InputNumber } from 'primereact/inputnumber'
import { ListBox } from 'primereact/listbox'

import { pressable } from '@aytm/app/stats/components/lib/pressable'
import OverlayPanel from '@aytm/app/stats/components/overlay-panel/OverlayPanel'
import AdvancedFilterContainer from '@aytm/app/stats/components/advanced-filter/AdvancedFilterContainer'

import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'
import AnswerVariant from '@aytm/app/stats/page/question-views/vq/components/AnswerVariant'
import AnswerVariantMX from '@aytm/app/stats/page/question-views/vq/components/AnswerVariantMX'
import AnswerVariantOEX from '@aytm/app/stats/page/question-views/vq/components/AnswerVariantOEX'

import Controls from '@aytm/app/stats/page/question-views/vq/components/Controls'
import Footer from '@aytm/app/stats/page/question-views/components/questions-custom/Footer'

// styles
import style from '@aytm/app/stats/page/question-views/vq/styles/Component.module.scss'

export const DataContext = React.createContext()

const BaseOptions = [
  { text: 'Entire sample (N)', key: 'number' },
  { text: 'Sum of all VQ answers', key: 'sum' },
  { text: 'Custom', key: 'expression' }
]

// Long enough for the Base list to answer that it is opening, short enough to read as immediate.
const PATTERN_CLEAR_DELAY = 400

const getDefaultAnswerVariant = (av = {}) => {
  return {
    key: generateUID(),
    text: '',
    pattern: '',
    base: '',
    mode: 'basic',
    other: false,
    ...av
  }
}

const Component = ({
  model,
  data,
  states,
  disabled,
  onChange,
  onControlsChange,
  onUpdateData,
  getTopsOptions,
  getLabelsOptions,
  getImagesOptions,
  getAnswerVariantsOptions
}) => {
  const [state, dispatch] = useReducer(dataReducer, {
    pattern: model.pattern,
    patternType: model.patternType,
    answerVariants: JSON.parse(JSON.stringify(model.answerVariants)),
    logicData: { pattern: [], items: {} },
    refs: {},
    errors: []
  })

  const [changed, setChanged] = useState(false)

  const timer = useRef(null)
  const pendingCommit = useRef(null)

  const stateRef = useRef()
  stateRef.current = state

  const dndItemsListRef = useRef()


  const dropdownsRef = useRef({})
  const parserResultPatternBasicsRef = useRef({})

  const patternTypeRef = useRef(null)
  const patternNumberRef = useRef(null)
  const patternExpressionRef = useRef(null)
  // The field for a base is only mounted once its type is picked, so the focus has to wait for it --
  // and for the area it sits in to finish opening, unless that area was open already.
  const patternFocusRef = useRef(false)
  const patternFocusOnSettleRef = useRef(false)
  // Leaving an emptied base clears the type -- but leaving it is also how the list is reached, and
  // the two blurs are the same one. So the clearing waits a moment, and the list cancels it by
  // opening; a pick made in a list already open cancels it too.
  const patternClearTimer = useRef(null)

  const mode = useMemo(() => {
    const answerVariants = state.answerVariants.filter(
      (_av, index) => null !== parserResultPatternBasicsRef.current[index + 1]
    )
    return !answerVariants.length || 'mx' == model.variant
      ? null
      : answerVariants.every(av => 'basic' == av.mode)
      ? 'basic'
      : 'advanced'
  }, [model.variant, state])
  const modeDisabled = useMemo(
    () =>
      null === mode ||
      !data.options.some(group => 0 < group.items.length) ||
      !state.answerVariants.some((_av, index) => null !== parserResultPatternBasicsRef.current[index + 1]) ||
      disabled,
    [data.options, mode, state, disabled]
  )

  const AnswerVariantComponent = useMemo(
    () =>
      'oex' == model.variant
        ? AnswerVariantOEX
        : 'mx' == model.variant
        ? AnswerVariantMX
        : AnswerVariant,
    [model.variant]
  )

  const answerVariantAmount = useMemo(() => state.answerVariants.length, [state])

  const answerVariantPrefix = useMemo(
    () => ('oex' == model.variant ? 'Q' : 'mx' == model.variant ? 'SQ' : 'A'),
    [model.variant]
  )
  const answerVariantAddText = useMemo(
    () =>
      `Add ${numberToWords(state.answerVariants.length + 1)} ${
        'oex' == model.variant ? 'Question' : 'mx' == model.variant ? 'Sub-Question' : 'Answer'
      }`,
    [model.variant, state]
  )

  const baseHasNumericBase = useMemo(
    () => state.answerVariants.some(av => av.base && 0 < av.base.length && !Number.isNaN(Number(av.base))),
    [state.answerVariants]
  )
  const baseHasAnyBase = useMemo(
    () => state.answerVariants.some(av => av.base && 0 < av.base.length),
    [state.answerVariants]
  )

  const baseOptions = useMemo(() => {
    if (baseHasNumericBase) {
      return BaseOptions.map(o => (['number', 'sum'].includes(o.key) ? Object.assign({}, o, { disabled: true }) : o))
    } else if (baseHasAnyBase) {
      return BaseOptions.map(o => (['number'].includes(o.key) ? Object.assign({}, o, { disabled: true }) : o))
    }
    return BaseOptions
  }, [baseHasNumericBase, baseHasAnyBase])

  // Reset pattern/patternType when a base change disables the currently-selected type — kept out of the memo so render stays side-effect-free
  useEffect(() => {
    const disabledByNumeric = baseHasNumericBase && ['number', 'sum'].includes(state.patternType)
    const disabledByAnyBase = !baseHasNumericBase && baseHasAnyBase && 'number' == state.patternType
    if (disabledByNumeric || disabledByAnyBase) {
      dispatch({ type: 'UPDATE_PATTERN', data: { pattern: '', patternType: null } })
    }
  }, [baseHasNumericBase, baseHasAnyBase, state.patternType])

  // Read per render rather than captured: the page stamps the roles once, but the suite fabricates
  // them, and a value taken at module load cannot follow that.
  const isAdmin = viewerIsAdmin()

  const basicFilters = useMemo(
    () =>
      new BasicFilters({
        clusters: data.clusters,
        surveySchema: data.surveySchema,
        longAllowed: true,
        tagsAllowed: ['rb', 'cb'].includes(model.variant),
        // sentimentsAllowed: ['rb', 'cb'].includes(model.variant),
        isAdmin
      }),
    [model.variant, data.surveySchema, data.clusters, isAdmin]
  )

  const parserResultEof = useMemo(
    () =>
      data.parser.analyzeString({
        filter: '',
        saveTokens: true
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.surveySchema]
  )
  const parserResultPattern = useMemo(
    () =>
      ('expression' == state.patternType &&
        data.parser.analyzeString({
          id: model.id,
          filter: state.pattern.trim(),
          clusters: data.clusters,
          saveTokens: true
        })) ||
      parserResultEof,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [model.id, data.surveySchema, data.clusters, state.pattern, state.patternType, parserResultEof]
  )
  const parserResultPatternError = useMemo(
    () =>
      'expression' == state.patternType &&
      0 < state.pattern.trim().length &&
      0 < parserResultPattern.expression.status.errors.length &&
      parserResultPattern.expression.status.errors.some(v => !['ref', 'cluster'].includes(v.type)),
    [state.pattern, state.patternType, parserResultPattern]
  )

  const onDNDPositionChange = useCallback(
    ({ newPosition, dragItemUpdater } = {}) => {
      dragItemUpdater(node => {
        const itemPositionNode = node.querySelector('.stats-VQ-AnswerVariant-index')
        if (null !== itemPositionNode) {
          itemPositionNode.innerHTML = null === newPosition ? '' : `${answerVariantPrefix}${newPosition + 1}.`
        }
      })
    },
    [answerVariantPrefix]
  )
  const onDNDUpdateItemPositions = useCallback(
    ({ hiddenItemIndex, placeholderIndex } = {}) => {
      const itemPositionNodes = dndItemsListRef.current.querySelectorAll(
        '.stats-VQ-AnswerVariant .stats-VQ-AnswerVariant-index'
      )

      for (const [i, itemPositionNode] of itemPositionNodes.entries()) {
        if (null !== itemPositionNode) {
          const visualItemIndex = i + (null !== hiddenItemIndex && hiddenItemIndex <= i ? -1 : 0)
          const newItemIndex =
            visualItemIndex + (null !== placeholderIndex && placeholderIndex <= visualItemIndex ? 1 : 0)
          itemPositionNode.innerHTML = null === newItemIndex ? '' : `${answerVariantPrefix}${newItemIndex + 1}.`
        }
      }
    },
    [answerVariantPrefix]
  )

  const onDNDDrop = res => {
    const { item, position, clone } = res

    if (clone) {
      dispatch({
        type: 'ADD_ANSWER_VARIANT',
        data: {
          item: {
            ...getDefaultAnswerVariant(item.item),
            key: generateUID()
          },
          position
        }
      })
    } else if (item.id != model.id) {
      item.remove()
      dispatch({
        type: 'ADD_ANSWER_VARIANT',
        data: {
          item: {
            ...getDefaultAnswerVariant(item.item),
            key: generateUID()
          },
          position
        }
      })
    } else if (item.position != position) {
      dispatch({
        type: 'REORDER_ANSWER_VARIANTS',
        data: {
          from: item.position,
          to: position
        }
      })
    }

    onDNDUpdateItemPositions()
  }

  const dndItemsList = ADND.targets
    .itemsList(
      {
        acceptTypes: [`dlq-answer-${PageGlobals.WINDOW_UID}`, `dlq-answer-other-${model.id}-${PageGlobals.WINDOW_UID}`],
        placeholderVMargin: 10
      },
      { capture: false }
    )
    .buildPlaceholder(() => {
      const placeholderNode = document.createElement('div')
      placeholderNode.className = `${style.placeholder}`
      placeholderNode.dataset.testid = 'stat-vq-answer-variant-placeholder'
      return placeholderNode
    })
    .onPositionChange(onDNDPositionChange)
    .onUpdateItemPositions(onDNDUpdateItemPositions)
    .onEnter(onDNDUpdateItemPositions)
    .onLeave(onDNDUpdateItemPositions)
    .onDrop(onDNDDrop)

  const checkModel = () => {
    const newData = {}
    if (state.pattern != model.pattern) newData.pattern = model.pattern
    if (state.patternType != model.patternType) newData.patternType = model.patternType
    if (!isEqual(state.answerVariants, model.answerVariants)) {
      newData.logicData = state.logicData
      newData.refs = state.refs
      newData.errors = JSON.parse(JSON.stringify(state.errors))
      for (const avi in state.answerVariants) {
        delete newData.logicData.items[`a${Number(avi) + 1}`]
        delete newData.refs[`a${Number(avi) + 1}`]
        const index = newData.errors.indexOf(`a${Number(avi) + 1}`)
        0 <= index && newData.errors.splice(index, 1)
      }
      newData.answerVariants = JSON.parse(JSON.stringify(model.answerVariants))
    }
    return newData
  }
  const checkState = () => {
    const newData = {}
    if (state.pattern != model.pattern) newData.pattern = state.pattern
    if (state.patternType != model.patternType) newData.patternType = state.patternType
    if (!isEqual(state.answerVariants, model.answerVariants))
      newData.answerVariants = JSON.parse(JSON.stringify(state.answerVariants))
    return newData
  }

  const checkError = () => {
    if (0 < state.errors.length || !state.answerVariants.length) return { error: true }
    if ('mx' == model.variant) {
      if (2 > state.answerVariants.length) return { error: true }
      const l = state.answerVariants.reduce((acc, cur) => {
        const option = data.options.flatMap(v => v.items).find(v => v.key == cur.pattern)
        if (option == undefined) return acc
        return [...acc, option.options.length].filter((v, i, a) => a.indexOf(v) == i)
      }, []).length
      if (1 != l) {
        return { error: 'Each sub-question must have the same number of answer options.' }
      }
    }
    return { error: false }
  }

  const onModeChange = () => {
    dispatch({
      type: 'UPDATE_ANSWER_VARIANTS_MODE',
      data: {
        positions: Object.keys(parserResultPatternBasicsRef.current).filter(
          k => null !== parserResultPatternBasicsRef.current[k]
        ),
        mode: 'basic' == mode ? 'advanced' : 'basic'
      }
    })
  }

  const onAddClick = () => {
    const { answerVariants } = state
    const index = answerVariants.findIndex(av => av.other)

    dispatch({
      type: 'ADD_ANSWER_VARIANT',
      data: {
        item: getDefaultAnswerVariant({ mode: mode || 'basic' }),
        position: 0 <= index && index + 1 == answerVariants.length ? index : answerVariants.length
      }
    })
  }

  const cancelPatternClear = () => {
    patternClearTimer.current && clearTimeout(patternClearTimer.current)
    patternClearTimer.current = null
  }

  const onPatternTypeChange = (patternType = null) => {
    dispatch({
      type: 'UPDATE_PATTERN',
      data: { pattern: '', patternType }
    })

    const opens = ['number', 'expression'].includes(patternType)

    patternFocusRef.current = opens
    patternFocusOnSettleRef.current = opens && !['number', 'expression'].includes(state.patternType)

    patternTypeRef.current.hide()
  }

  const onPatternChange = (value = '') => {
    dispatch({
      type: 'UPDATE_PATTERN',
      data: { pattern: value }
    })
  }

  const onPatternBlur = (value = '') => {
    if (0 < String(value ?? '').trim().length) return

    cancelPatternClear()
    patternClearTimer.current = setTimeout(() => {
      patternClearTimer.current = null
      patternFocusRef.current = false

      dispatch({
        type: 'UPDATE_PATTERN',
        data: { pattern: '', patternType: null }
      })
    }, PATTERN_CLEAR_DELAY)
  }

  const onOtherChange = () => {
    const answerVariants = state.answerVariants
    const index = answerVariants.findIndex(av => av.other)

    0 > index
      ? dispatch({
          type: 'ADD_ANSWER_VARIANT',
          data: {
            item: getDefaultAnswerVariant({
              key: data.otherUID,
              text: 'Other',
              other: true
            }),
            position: answerVariants.length
          }
        })
      : dispatch({
          type: 'DELETE_ANSWER_VARIANT',
          data: index
        })
  }

  useEffect(() => {
    dispatch({
      type: 'UPDATE_LOGIC_DATA',
      data: {
        id: 'pattern',
        logicData: getLogicData(data.tags, data.codeBooks, parserResultPattern, parserResultPatternError)
      }
    })
  }, [data.tags, data.codeBooks, parserResultPattern, parserResultPatternError])
  useEffect(() => {
    const patternRefs = getRefs(parserResultPattern)
    const clustersRefs = getClustersRefs(data.modelClusters, parserResultPattern)

    dispatch({
      type: 'UPDATE_REFS',
      data: {
        id: `pattern`,
        refs: [patternRefs, clustersRefs]
      }
    })
  }, [data.modelClusters, parserResultPattern])
  useEffect(() => {
    dispatch({
      type: 'UPDATE_ERRORS',
      data: {
        id: 'pattern',
        error: parserResultPatternError
      }
    })
  }, [parserResultPatternError])

  const focusPatternField = () => {
    if (!patternFocusRef.current) return
    patternFocusRef.current = false

    const field = 'number' == state.patternType ? patternNumberRef.current : patternExpressionRef.current
    field?.focus()
  }

  useEffect(() => cancelPatternClear, [])

  useEffect(() => {
    if (!patternFocusOnSettleRef.current) focusPatternField()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.patternType])

  useEffect(() => {
    dndItemsList.connect(dndItemsListRef.current)

    return () => {
      dndItemsList.disconnect()
    }
  }, [dndItemsList])

  useEffect(() => {
    if (changed) return

    dispatch({
      type: 'RESET',
      data: { ...checkModel() }
    })

    setChanged(
      stateRef.current.variant != model.variant ||
        stateRef.current.pattern != model.pattern ||
        stateRef.current.patternType != model.patternType ||
        !isEqual(stateRef.current.answerVariants, model.answerVariants)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.pattern, model.patternType, model.answerVariants])

  useEffect(() => {
    setChanged(true)

    const newData = {
      logicData: state.logicData,
      refs: state.refs,
      ...checkError(),
      ...checkState()
    }

    timer.current && clearTimeout(timer.current)
    const commit = () => {
      timer.current = null
      pendingCommit.current = null

      if (Object.hasOwn(newData, 'answerVariants')) {
        dispatchEvent(
          new CustomEvent('customQuestionChanged', {
            detail: {
              id: model.id.toUpperCase(),
              items: newData.answerVariants.reduce((acc, cur, i) => {
                switch (model.variant) {
                  case 'mx': {
                    const option = data.options.flatMap(v => v.items).find(v => v.key == cur.pattern)
                    acc[cur.key] = {
                      key: `SQ${i + 1}`,
                      size: option != undefined ? option.options.length : 0
                    }
                    break
                  }
                  default: {
                    acc[cur.key] = `A${i + 1}`
                  }
                }
                return acc
              }, {}),
              delay: newData.answerVariants.length > model.answerVariants.length
            }
          })
        )
      }

      onChange(newData)

      setChanged(false)
    }
    pendingCommit.current = commit
    timer.current = setTimeout(commit, 300)

    return () => {
      timer.current && clearTimeout(timer.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  // a pending edit landing after a variant switch would restore what the switch dropped
  const onControlsChangeFlushed = useCallback(
    (key, value) => {
      if ('variant' == key && timer.current) {
        clearTimeout(timer.current)
        pendingCommit.current?.()
      }
      onControlsChange(key, value)
    },
    [onControlsChange]
  )

  const dataContextValue = useMemo(() => {
    return {
      id: model.id,
      data,
      disabled,
      dispatch,
      dropdownsRef,
      parserResultPatternBasicsRef,
      basicFilters,
      parserResultEof,
      answerVariantAmount
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    model.id,
    data,
    disabled,
    dispatch,
    dropdownsRef,
    parserResultPatternBasicsRef,
    basicFilters,
    parserResultEof,
    answerVariantAmount
  ])

  const renderPatternOptions = () => (
    <>
      <div className="p-right p-col">
        <OverlayPanel
          ref={patternTypeRef}
          onShow={cancelPatternClear}
          button={
            <span className={style['pattern-type']}>
              {state.patternType
                ? `Base: ${baseOptions.find(option => option.key == state.patternType).text}`
                : 'Base: N'}{' '}
              <span className="p-c pi pi-chevron-down" />
            </span>
          }
        >
          <ListBox
            options={baseOptions}
            optionValue="key"
            optionLabel="text"
            onChange={disabled ? null : event => onPatternTypeChange(event.value)}
          />
        </OverlayPanel>
      </div>
      <Transition in={['number', 'expression'].includes(state.patternType)} timeout={500} onEntered={focusPatternField}>
        {status => (
          <div
            className={`${style['transition-base']} ${status ? style['transition-base-' + status] : ''} p-right p-col`}
          >
            <div className="p-fluid p-col-12">
              {'number' == state.patternType && (
                <InputNumber
                  className="p-inputnumber-rounded"
                  aria-label="Base number"
                  inputRef={patternNumberRef}
                  inputClassName="p-inputtext-rounded"
                  placeholder=""
                  min={0}
                  mode="decimal"
                  value={state.pattern || null}
                  showButtons
                  useGrouping={false}
                  disabled={disabled}
                  onChange={disabled ? null : event => onPatternChange(String(event.value || ''))}
                  onBlur={disabled ? null : event => onPatternBlur(event?.target?.value)}
                />
              )}
              {'expression' == state.patternType && (
                <AdvancedFilterContainer
                  ref={patternExpressionRef}
                  className="p-component p-inputtext p-inputtext-rounded"
                  pattern={state.pattern || ''}
                  parser={data.parser}
                  surveySchema={data.surveySchema}
                  clusters={data.clusters}
                  parserResultEof={parserResultEof}
                  hintPosition="top"
                  disabled={disabled}
                  onChange={disabled ? null : onPatternChange}
                  onBlur={disabled ? null : onPatternBlur}
                />
              )}
            </div>
          </div>
        )}
      </Transition>
    </>
  )

  return (
    <BlockWrapper>
      <ComponentContainer
        Controls={Controls}
        model={model}
        data={data}
        states={states}
        changed={changed}
        disabled={disabled}
        onChange={onControlsChangeFlushed}
        getTopsOptions={getTopsOptions}
        getLabelsOptions={getLabelsOptions}
        getImagesOptions={getImagesOptions}
        getAnswerVariantsOptions={getAnswerVariantsOptions}
      >
        <div className={style.component}>
          {modeDisabled ? null : (
            <div className={style.row}>
              <span
                data-testid={`stat-vq-switch-all-${model.id}`}
                className={style.toggle}
                {...pressable(onModeChange)}
              >
                switch ALL to {'basic' == mode ? 'advanced' : 'basic'} mode
              </span>
            </div>
          )}
          <div data-testid={`stat-vq-answer-variants-${model.id}`} ref={dndItemsListRef} className={style.list}>
            <DataContext.Provider value={dataContextValue}>
              {state.answerVariants.map((item, index) => (
                <AnswerVariantComponent key={item.key} item={item} position={index + 1} dndItemsList={dndItemsList} />
              ))}
            </DataContext.Provider>
          </div>
          {!state.pattern.length && 'sum' != state.patternType && disabled ? null : (
            <div className={style.row}>
              {disabled ? null : (
                <>
                  <div className={style.index}>
                    {answerVariantPrefix}
                    {state.answerVariants.length + 1}.
                  </div>
                  <div className="p-field">
                    <span data-testid={`stat-vq-add-${model.id}`} className={style.add} {...pressable(onAddClick)}>
                      {answerVariantAddText}
                    </span>
                  </div>
                  {['mx', 'oex'].includes(model.variant) ? null : (
                    <div
                      data-testid={`stat-vq-everyone-else-${model.id}`}
                      className={`${style.group} ${style['group-checkbox']}`}
                    >
                      <Checkbox
                        aria-labelledby={`stat-vq-everyone-else-${model.id}-label`}
                        value="other"
                        checked={state.answerVariants.some(av => av.other)}
                        onChange={onOtherChange}
                      />
                      <span
                        id={`stat-vq-everyone-else-${model.id}-label`}
                        className={classNames('p-checkbox-label', style['group-checkbox-label'])}
                        onClick={onOtherChange}
                      >
                        Everyone else
                      </span>
                    </div>
                  )}
                </>
              )}
              {['mx', 'oex'].includes(model.variant) ? null : renderPatternOptions()}
            </div>
          )}
        </div>
        {disabled ? null : <Footer model={model} states={states} onUpdateData={onUpdateData} />}
      </ComponentContainer>
    </BlockWrapper>
  )
}

Component.propTypes = {
  model: PropTypes.object,
  data: PropTypes.object,
  states: PropTypes.object,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onControlsChange: PropTypes.func,
  onUpdateData: PropTypes.func,
  getTopsOptions: PropTypes.func,
  getLabelsOptions: PropTypes.func,
  getImagesOptions: PropTypes.func,
  getAnswerVariantsOptions: PropTypes.func
}

Component.displayName = 'Component'

export default Component
